import { createGroupBillingCode, deleteGroupBillingCode, listGroupBillingCodes, listReportBillingCodes, patchGroupBillingCode, } from '@/lib/api/teleconsultation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
const billingCodesKey = (groupId, active = 'true') => ['tc', 'groups', groupId, 'billing-codes', active];
// Report-scoped catalog (what the report editor's picker shows). Keyed by report id so a
// case reassigned to another group, or a code created inline, refetches the right list.
const reportBillingCodesKey = (reportId) => ['tc', 'report-billing-codes', reportId];
/** Invalidate every catalog listing (settings tab + every open report picker). */
function invalidateBillingCatalogs(qc, groupId) {
    void qc.invalidateQueries({ queryKey: ['tc', 'groups', groupId, 'billing-codes'] });
    void qc.invalidateQueries({ queryKey: ['tc', 'report-billing-codes'] });
}
export function useBillingCodes(groupId, active = 'true') {
    return useQuery({
        queryKey: groupId ? billingCodesKey(groupId, active) : ['tc', 'billing-codes', 'idle'],
        queryFn: () => listGroupBillingCodes(groupId, active),
        enabled: !!groupId,
        staleTime: 60_000,
    });
}
/**
 * The codes the picker may attach to THIS report, resolved server-side from the report's
 * case (its assigned group). Prefer this over `useBillingCodes(groupId)` inside the report
 * editor: it needs no client-side group id and is authorized through the same TC-group arm
 * as the report itself, so a receiver who is not a member of the submitting clinic still
 * sees their group's codes.
 */
export function useReportBillingCodes(reportId) {
    return useQuery({
        queryKey: reportId ? reportBillingCodesKey(reportId) : ['tc', 'report-billing-codes', 'idle'],
        queryFn: () => listReportBillingCodes(reportId),
        enabled: !!reportId,
        staleTime: 60_000,
    });
}
export function useCreateBillingCode(groupId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            if (!groupId)
                throw new Error('No group');
            return createGroupBillingCode(groupId, {
                ...data,
                defaultForAnatomy: data.defaultForAnatomy ?? [],
            });
        },
        onSuccess: () => {
            if (groupId)
                invalidateBillingCatalogs(qc, groupId);
        },
    });
}
export function useEditBillingCode(groupId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ codeId, patch, }) => {
            if (!groupId)
                throw new Error('No group');
            return patchGroupBillingCode(groupId, codeId, patch);
        },
        onSuccess: () => {
            if (groupId)
                invalidateBillingCatalogs(qc, groupId);
        },
    });
}
export function useDeleteBillingCode(groupId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (codeId) => {
            if (!groupId)
                throw new Error('No group');
            return deleteGroupBillingCode(groupId, codeId);
        },
        onSuccess: () => {
            if (groupId)
                invalidateBillingCatalogs(qc, groupId);
        },
    });
}
